import React, { useEffect, useMemo, useRef, useState } from 'react'
import parse from 'autosuggest-highlight/parse'
import throttle from 'lodash/throttle'
import Autocomplete from '@material-ui/lab/Autocomplete'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import {
  Grid,
  Typography,
  makeStyles,
  TextField,
  Popper,
} from '@material-ui/core'
import { useJsApiLoader } from '@react-google-maps/api'
import { CONFIG } from 'Config/config'
import { formatAddress } from './utils'
import { libraries } from './options'

import { styles } from './style'

const useStyles = makeStyles(() => styles)

export const PlaceAutocomplete = ({
  city = undefined,
  countryCode = null,
  disabled = false,
  key = undefined,
  restrictedIds = undefined,
  placeholder,
  onPlaceChanged = () => {},
  noOptionText = 'Start typing to see suggestions',
  disableAutoClear = true,
  defaultValue = null,
  inputValue = '',
  setInputValue = () => {},
  // setIsAddressError,
}) => {
  const classes = useStyles()
  const [value, setValue] = useState(
    defaultValue ? { description: defaultValue } : null,
  )
  const [options, setOptions] = useState([])
  const [popperWidth, setPopperWidth] = useState(100)
  const manualInputRef = useRef(false)
  const didInitRef = useRef(false)

  const autocompleteService = useRef(null)
  const placesService = useRef(null)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: CONFIG.GOOGLE_API_KEY,
    libraries,
    language: 'en',
  })

  useEffect(() => {
    if (!didInitRef.current && defaultValue) {
      const newValue = { description: defaultValue }
      setValue(newValue)
      setInputValue(defaultValue)
      setOptions([newValue])
      didInitRef.current = true
    }
  }, [defaultValue])

  useEffect(() => {
    if (inputValue) {
      const newValue = { description: inputValue }
      setValue(newValue)
      setOptions([newValue])
    }
  }, [inputValue])

  // Initialize services when API is loaded
  useEffect(() => {
    if (isLoaded && window.google && window.google.maps) {
      try {
        window.google.maps
          .importLibrary('places')
          .then(() => {
            if (!autocompleteService.current) {
              autocompleteService.current = new window.google.maps.places.AutocompleteService()
            }
            if (!placesService.current) {
              const map = document.createElement('div')
              placesService.current = new window.google.maps.places.PlacesService(
                map,
              )
            }
          })
          .catch(error => {
            console.error('Error loading places library:', error)
          })
      } catch (error) {
        console.error('Error initializing Google Maps services:', error)
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
        })
      }
    }
  }, [isLoaded])

  const resetAllValues = () => {
    setValue(null)
    setInputValue('')
    setOptions([])
  }

  const fetchPlacePredictions = useMemo(
    () =>
      throttle((request, callback) => {
        if (autocompleteService.current) {
          autocompleteService.current.getPlacePredictions(request, callback)
        }
      }, 200),
    [],
  )

  const getPlaceDetails = placeDetails => {
    const formattedAddress = formatAddress(placeDetails.address_components)
    // if (formattedAddress === 'Enter address manually') {
    //   setIsAddressError('Address not found, please put it manually')
    // } else {
    //   setIsAddressError(null)
    // }

    const newValue = { description: formattedAddress }
    onPlaceChanged({
      formattedAddress,
      geometry: {
        location: {
          lat: () =>
            (placeDetails &&
              placeDetails.geometry &&
              placeDetails.geometry.location &&
              placeDetails.geometry.location.lat()) ||
            0,
          lng: () =>
            (placeDetails &&
              placeDetails.geometry &&
              placeDetails.geometry.location &&
              placeDetails.geometry.location.lng()) ||
            0,
        },
      },
    })

    setValue(newValue)
    setInputValue(formattedAddress)
    setOptions([newValue])
  }

  const handleAutocompleteChange = (event, newValue) => {
    if (!placesService.current) {
      try {
        const map = document.createElement('div')
        placesService.current = new window.google.maps.places.PlacesService(map)
      } catch (error) {
        return undefined
      }
    }

    if (newValue) {
      if (!newValue.isPostcode && newValue.place_id) {
        const request = {
          placeId: newValue.place_id,
        }
        placesService.current.getDetails(request, getPlaceDetails)
      } else if (newValue.description) {
        onPlaceChanged({
          formattedAddress: newValue.description,
          geometry: { location: { lat: () => 0, lng: () => 0 } },
        })
        setValue(newValue)
        setInputValue(newValue.description)
        setOptions([newValue])
      }
    }

    if (!disableAutoClear) {
      resetAllValues()
    }
  }

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'input') {
      manualInputRef.current = true
    }
    setInputValue(newInputValue)
    if (!newInputValue) {
      resetAllValues()
      return
    }
    if (!autocompleteService.current || newInputValue === '') {
      return undefined
    }
    const request = {
      input: city ? `${city.name}, ${newInputValue}` : newInputValue,
      language: 'en',
      componentRestrictions: { country: countryCode },
    }
    fetchPlacePredictions(request, results => {
      let newOptions = []
      if (results) {
        newOptions = [...results]
      }
      if (restrictedIds && restrictedIds.length) {
        newOptions = newOptions.filter(
          newOption => !restrictedIds.includes(newOption.place_id),
        )
      }
      setOptions([...newOptions])
    })
  }

  const noOptionsTextRender = customText => {
    if (options.length === 0) {
      if (!inputValue) {
        return customText
      } else {
        return 'No options found'
      }
    }
  }

  return isLoaded ? (
    <Autocomplete
      id={key}
      onLoad={!isLoaded}
      className={classes.placesAutocomplete}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={x => x}
      options={options}
      autoComplete
      blurOnSelect={!disableAutoClear}
      noOptionsText={noOptionsTextRender(noOptionText)}
      clearOnBlur={!disableAutoClear}
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={handleAutocompleteChange}
      onInputChange={handleInputChange}
      disabled={disabled}
      PopperComponent={props => (
        <Popper
          {...props}
          style={{
            zIndex: 9999,
            width: popperWidth ? `${popperWidth}px` : undefined,
          }}
        />
      )}
      popupIcon={null}
      clearIcon={null}
      renderInput={params => {
        const inputWidth =
          params &&
          params.inputProps &&
          params.inputProps.ref &&
          params.inputProps.ref.current &&
          params.inputProps.ref.current.offsetWidth
        if (inputWidth) {
          setPopperWidth(inputWidth)
        }

        return (
          <TextField
            {...params}
            value={inputValue}
            onChange={e => {
              setInputValue(e.target.value)
              handleInputChange(e, e.target.value, 'input')
            }}
            placeholder={placeholder}
            variant="outlined"
            size="small"
            fullWidth
            className={classes.customInput}
          />
        )
      }}
      renderOption={option => {
        if (!option.isPostcode) {
          const matches =
            (option.structured_formatting &&
              option.structured_formatting.main_text_matched_substrings) ||
            []
          const parts = parse(
            (option.structured_formatting &&
              option.structured_formatting.main_text) ||
              '',
            matches.map(match => [match.offset, match.offset + match.length]),
          )

          return (
            <Grid container alignItems="center">
              <Grid item>
                <LocationOnIcon className={classes.icon} />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{ fontWeight: part.highlight ? 700 : 400 }}
                  >
                    {part.text}
                  </span>
                ))}

                <Typography variant="body2" color="textSecondary">
                  {(option.structured_formatting &&
                    option.structured_formatting.secondary_text) ||
                    ''}
                </Typography>
              </Grid>
            </Grid>
          )
        }

        return (
          <Grid container alignItems="center">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              <Typography style={{ fontWeight: 700 }}>
                {option.description}
              </Typography>
            </Grid>
          </Grid>
        )
      }}
    />
  ) : (
    <div>Google Places API Loading...</div>
  )
}
